import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { departmentAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";

export default function DepartmentForm({
  department,
  onClose,
  onSuccess,
  isOpen = false,
}) {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    manager_id: "",
    budget: "",
    actif: true,
  });
  const [managers, setManagers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(true);

  // Load managers and set form data
  useEffect(() => {
    fetchManagers();
    if (department) {
      setFormData({
        nom: department.nom || "",
        description: department.description || "",
        manager_id: department.manager_id || "",
        budget: department.budget || "",
        actif: department.actif !== undefined ? department.actif : true,
      });
    }
  }, [department]);

  const fetchManagers = async () => {
    try {
      setLoadingManagers(true);
      const response = await departmentAPI.getAvailableManagers();
      if (response.data.success) {
        setManagers(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching managers:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors du chargement des managers",
        variant: "destructive",
      });
    } finally {
      setLoadingManagers(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Nom validation
    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom du département est requis";
    } else if (formData.nom.trim().length < 2) {
      newErrors.nom = "Le nom doit contenir au moins 2 caractères";
    }

    // Budget validation
    if (
      formData.budget &&
      (isNaN(formData.budget) || parseFloat(formData.budget) < 0)
    ) {
      newErrors.budget = "Le budget doit être un nombre positif";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        nom: formData.nom.trim(),
        description: formData.description.trim() || null,
        manager_id: formData.manager_id || null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        actif: formData.actif,
      };

      let response;
      if (department) {
        response = await departmentAPI.update(department.id, submitData);
      } else {
        response = await departmentAPI.create(submitData);
      }

      if (response.data.success) {
        toast({
          title: "Succès",
          description: department
            ? "Département mis à jour avec succès"
            : "Département créé avec succès",
          variant: "success",
        });
        onSuccess();
      }
    } catch (error) {
      console.error("Error submitting department:", error);
      const message =
        error.response?.data?.message || "Une erreur s'est produite";
      toast({
        title: "Erreur",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {department ? "Modifier le département" : "Nouveau département"}
          </DialogTitle>
          <DialogDescription>
            {department
              ? "Modifiez les informations du département"
              : "Créez un nouveau département pour votre organisation"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nom */}
          <div className="space-y-2">
            <Label htmlFor="nom">
              Nom du département <span className="text-red-500">*</span>
            </Label>
            <Input
              id="nom"
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange("nom", e.target.value)}
              placeholder="Ex: Ressources Humaines"
              className={errors.nom ? "border-red-500" : ""}
            />
            {errors.nom && <p className="text-sm text-red-500">{errors.nom}</p>}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Description du département (optionnel)"
              rows={3}
            />
          </div>

          {/* Manager */}
          <div className="space-y-2">
            <Label htmlFor="manager">Manager</Label>
            {loadingManagers ? (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
                <span>Chargement des managers...</span>
              </div>
            ) : (
              <Select
                value={formData.manager_id || "none"}
                onValueChange={(value) =>
                  handleInputChange("manager_id", value === "none" ? "" : value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un manager (optionnel)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun manager</SelectItem>
                  {managers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      {manager.prenom} {manager.nom} - {manager.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Budget */}
          <div className="space-y-2">
            <Label htmlFor="budget">Budget (€)</Label>
            <Input
              id="budget"
              type="number"
              step="0.01"
              min="0"
              value={formData.budget}
              onChange={(e) => handleInputChange("budget", e.target.value)}
              placeholder="Ex: 50000"
              className={errors.budget ? "border-red-500" : ""}
            />
            {errors.budget && (
              <p className="text-sm text-red-500">{errors.budget}</p>
            )}
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Statut</Label>
            <Select
              value={formData.actif ? "true" : "false"}
              onValueChange={(value) =>
                handleInputChange("actif", value === "true")
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner le statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Actif</SelectItem>
                <SelectItem value="false">Inactif</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {department ? "Modification..." : "Création..."}
                </>
              ) : department ? (
                "Modifier"
              ) : (
                "Créer"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
